/**
 * Route Permission Configuration
 *
 * Maps route paths to required permissions.
 * Format: path pattern -> required permission (resource:action)
 *
 * The permission check uses the resource:read action for view access.
 * For create/update routes, the appropriate action is checked.
 */

import type { IUserPermission } from '@/types/components';

type RoutePermissionConfig = {
  pattern: RegExp;
  permission: string;
};

/**
 * Route permission mappings.
 * Order matters - more specific patterns should come first.
 */
export const routePermissions: RoutePermissionConfig[] = [
  // Dashboard - accessible to all authenticated users
  { pattern: /^\/$/, permission: '' },
  { pattern: /^\/dashboard$/, permission: '' },
  { pattern: /^\/me$/, permission: '' },

  // Products
  { pattern: /^\/products\/create$/, permission: 'product:create' },
  { pattern: /^\/products\/update\//, permission: 'product:update' },
  { pattern: /^\/products\/bestsellers$/, permission: 'product:read' },
  { pattern: /^\/products$/, permission: 'product:read' },

  // Bundles
  { pattern: /^\/bundles\/create$/, permission: 'bundle:create' },
  { pattern: /^\/bundles\/update\//, permission: 'bundle:update' },
  { pattern: /^\/bundles$/, permission: 'bundle:read' },

  // Categories
  { pattern: /^\/categories\/create$/, permission: 'category:create' },
  { pattern: /^\/categories\/update\//, permission: 'category:update' },
  { pattern: /^\/categories$/, permission: 'category:read' },

  // Orders
  { pattern: /^\/orders\/update\//, permission: 'order:update' },
  { pattern: /^\/orders$/, permission: 'order:read' },

  // Content
  { pattern: /^\/content\/create$/, permission: 'content:create' },
  { pattern: /^\/content\/update\//, permission: 'content:update' },
  { pattern: /^\/content\/ailink\//, permission: 'content:update' },
  { pattern: /^\/content$/, permission: 'content:read' },

  // Content Calendar
  { pattern: /^\/content-calendar$/, permission: 'content-calendar:read' },

  // Content Categories
  {
    pattern: /^\/content-categories\/create$/,
    permission: 'content-category:create',
  },
  {
    pattern: /^\/content-categories\/update\//,
    permission: 'content-category:update',
  },
  { pattern: /^\/content-categories$/, permission: 'content-category:read' },

  // Content Types
  { pattern: /^\/content-types\/create$/, permission: 'content-type:create' },
  { pattern: /^\/content-types\/update\//, permission: 'content-type:update' },
  { pattern: /^\/content-types$/, permission: 'content-type:read' },

  // Visual Content
  {
    pattern: /^\/visual-content\/create$/,
    permission: 'visual-content:create',
  },
  {
    pattern: /^\/visual-content\/update\//,
    permission: 'visual-content:update',
  },
  { pattern: /^\/visual-content$/, permission: 'visual-content:read' },

  // Social Content
  {
    pattern: /^\/social-content\/create$/,
    permission: 'social-content:create',
  },
  {
    pattern: /^\/social-content\/update\//,
    permission: 'social-content:update',
  },
  { pattern: /^\/social-content$/, permission: 'social-content:read' },

  // Content Briefs
  { pattern: /^\/content-briefs\/create$/, permission: 'content-brief:create' },
  {
    pattern: /^\/content-briefs\/update\//,
    permission: 'content-brief:update',
  },
  { pattern: /^\/content-briefs$/, permission: 'content-brief:read' },

  // Pages
  { pattern: /^\/pages\/create$/, permission: 'page:create' },
  { pattern: /^\/pages\/update\//, permission: 'page:update' },
  { pattern: /^\/pages$/, permission: 'page:read' },

  // Customers
  { pattern: /^\/customers\/update\//, permission: 'customer:update' },
  { pattern: /^\/customers$/, permission: 'customer:read' },

  // Influencers
  { pattern: /^\/influencers\/create$/, permission: 'influencer:create' },
  { pattern: /^\/influencers\/update\//, permission: 'influencer:update' },
  { pattern: /^\/influencers$/, permission: 'influencer:read' },

  // Invitations
  { pattern: /^\/invitations\/create$/, permission: 'invitation:create' },
  { pattern: /^\/invitations\/update\//, permission: 'invitation:update' },
  { pattern: /^\/invitations$/, permission: 'invitation:read' },

  // Tips
  { pattern: /^\/tips\/create$/, permission: 'tip:create' },
  { pattern: /^\/tips\/update\//, permission: 'tip:update' },
  { pattern: /^\/tips$/, permission: 'tip:read' },

  // Conversations
  { pattern: /^\/conversations\/update\//, permission: 'conversation:update' },
  { pattern: /^\/conversations$/, permission: 'conversation:read' },

  // Conversation Feedbacks
  {
    pattern: /^\/conversation-feedbacks$/,
    permission: 'conversation-feedback:read',
  },

  // Feedbacks
  { pattern: /^\/feedbacks\/update\//, permission: 'feedback:update' },
  { pattern: /^\/feedbacks$/, permission: 'feedback:read' },

  // Team Members
  { pattern: /^\/team-members\/create$/, permission: 'team-member:create' },
  { pattern: /^\/team-members\/update\//, permission: 'team-member:update' },
  { pattern: /^\/team-members$/, permission: 'team-member:read' },

  // Subscribers
  { pattern: /^\/subscribers$/, permission: 'subscriber:read' },

  // Suggested Questions
  {
    pattern: /^\/suggested-questions\/create$/,
    permission: 'suggested-question:create',
  },
  {
    pattern: /^\/suggested-questions\/update\//,
    permission: 'suggested-question:update',
  },
  { pattern: /^\/suggested-questions$/, permission: 'suggested-question:read' },

  // System - Users
  { pattern: /^\/system\/users\/create$/, permission: 'user:create' },
  { pattern: /^\/system\/users\/update\//, permission: 'user:update' },
  { pattern: /^\/system\/users$/, permission: 'user:read' },

  // System - Permissions
  { pattern: /^\/system\/permissions/, permission: 'permission:read' },
  { pattern: /^\/permissions/, permission: 'permission:read' },

  // System - Files
  { pattern: /^\/system\/files$/, permission: 'file:read' },

  // System - Settings
  { pattern: /^\/system\/settings$/, permission: 'setting:read' },

  // Activity Logs
  { pattern: /^\/activity-logs$/, permission: 'activity-log:read' },
];

/**
 * Get the required permission for a given path.
 * Returns empty string if no permission is required (public routes).
 * Returns null if the path doesn't match any configured route (should show 404).
 */
export function getRequiredPermission(pathname: string): string | null {
  for (const config of routePermissions) {
    if (config.pattern.test(pathname)) {
      return config.permission;
    }
  }
  // No matching route found - let the router handle it (404)
  return null;
}

/**
 * Check if a user has permission to access a route.
 * @param pathname - The route pathname
 * @param userPermissions - Array of user's permission objects
 * @returns true if access is allowed, false if denied
 */
export function hasRoutePermission(
  pathname: string,
  userPermissions: IUserPermission[],
): boolean {
  const requiredPermission = getRequiredPermission(pathname);

  // No matching route or no permission required
  if (requiredPermission === null || requiredPermission === '') {
    return true;
  }

  // Check for wildcard permission (superadmin)
  const hasWildcard = userPermissions.some(
    p => p.resource_name === '*' && p.action === '*',
  );
  if (hasWildcard) {
    return true;
  }

  // Parse required permission (format: resource:action)
  const [requiredResource, requiredAction] = requiredPermission.split(':');

  // Check specific permission
  return userPermissions.some(
    p => p.resource_name === requiredResource && p.action === requiredAction,
  );
}
